import { useCallback, useState } from "react";
import type { z } from "zod";
import type { FieldError } from "@/lib/api/error-parser";

/**
 * Form validation state
 */
export interface ValidationState {
	/** Field-level errors from client-side validation */
	errors: Record<string, string | undefined>;
	/** Fields that have been touched/blurred */
	touched: Record<string, boolean>;
}

/**
 * Hook for managing form validation with Zod schemas
 */
export function useFormValidation<T extends z.ZodType>(schema: T) {
	const [validationState, setValidationState] = useState<ValidationState>({
		errors: {},
		touched: {},
	});

	/**
	 * Validate entire form data
	 * Returns true if valid, false otherwise
	 */
	const validate = useCallback(
		(data: unknown): boolean => {
			const result = schema.safeParse(data);

			if (result.success) {
				setValidationState({ errors: {}, touched: {} });
				return true;
			}

			// Convert Zod errors to field errors
			const errors: Record<string, string | undefined> = {};
			for (const error of result.error.issues) {
				const field = error.path.join(".");
				errors[field] = error.message;
			}

			setValidationState((prev) => ({
				...prev,
				errors,
			}));

			return false;
		},
		[schema]
	);

	/**
	 * Validate a single field
	 * Useful for on-blur validation
	 */
	const validateField = useCallback(
		(fieldName: string, _value: unknown, formData: unknown): void => {
			// Mark field as touched
			setValidationState((prev) => ({
				...prev,
				touched: {
					...prev.touched,
					[fieldName]: true,
				},
			}));

			// Validate the entire form to get field-specific error
			const result = schema.safeParse(formData);

			if (result.success) {
				// Clear error for this field
				setValidationState((prev) => ({
					...prev,
					errors: {
						...prev.errors,
						[fieldName]: undefined,
					},
				}));
				return;
			}

			// Find error for this specific field
			const fieldError = result.error.issues.find((e) => e.path.join(".") === fieldName);

			setValidationState((prev) => ({
				...prev,
				errors: {
					...prev.errors,
					[fieldName]: fieldError?.message || undefined,
				},
			}));
		},
		[schema]
	);

	/**
	 * Set server-side errors (from API 422 responses)
	 */
	const setServerErrors = useCallback((fieldErrors: FieldError[]): void => {
		const errors: Record<string, string | undefined> = {};
		for (const error of fieldErrors) {
			errors[error.field] = error.message;
		}

		setValidationState((prev) => ({
			...prev,
			errors: {
				...prev.errors,
				...errors,
			},
		}));
	}, []);

	/**
	 * Clear all errors
	 */
	const clearErrors = useCallback((): void => {
		setValidationState({ errors: {}, touched: {} });
	}, []);

	/**
	 * Clear error for a specific field
	 */
	const clearFieldError = useCallback((fieldName: string): void => {
		setValidationState((prev) => ({
			...prev,
			errors: {
				...prev.errors,
				[fieldName]: undefined,
			},
		}));
	}, []);

	/**
	 * Get error message for a field (only if touched)
	 */
	const getFieldError = useCallback(
		(fieldName: string): string | undefined => {
			// Only show error if field has been touched or if it's a server error
			return validationState.errors[fieldName];
		},
		[validationState.errors]
	);

	/**
	 * Check if form has any errors
	 */
	const hasErrors = Object.keys(validationState.errors).some((key) => validationState.errors[key]);

	return {
		errors: validationState.errors,
		touched: validationState.touched,
		validate,
		validateField,
		setServerErrors,
		clearErrors,
		clearFieldError,
		getFieldError,
		hasErrors,
	};
}
